import type { ReactNode } from 'react'

import { CHART_HEAD, CHART_UNDER_HEAD } from '@/components/charts/chart-header-layout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import StatStripSkeleton from '@/components/ui/StatStripSkeleton'
import { FEATURED_BASKETS } from '@/features/baskets/api/basketsService'
import { BRIEF_API_AVAILABLE } from '@/features/brief/api/briefService'
import { cn } from '@/lib/utils'
import { Line } from '@/screens/app/components/PendingBlocks'
import { PanelRowsSkeleton } from '@/screens/app/home/components/HomePanel'

/*
 * Home before its data: the route's pending page, block for block, and the
 * body skeletons the cards draw inside their own CardState once the page has
 * committed — one set of shapes for both, so a card never changes size
 * between the pending page and its own skeleton, or between its skeleton and
 * its rows. Its own light module for the reason PendingBlocks.tsx gives.
 *
 * Each block reuses the loaded card's own box, to the Home mock (2026-10-28):
 * Mitra beside the bordered bubble for the brief; the hairline panel and its
 * 20px inset for Your portfolio; `HomePanel`'s 40px head band and its own
 * `PanelRowsSkeleton` for Today's news and Company announcements;
 * `DataTableSkeleton` (the shared table panel, its 36px head band, 44px rows
 * for the movers and 56px two-line rows for the issues) under a 28px head row;
 * `StatStripSkeleton ruled` for the NEPSE strip. Baskets to try draws its
 * cards' skeleton, though the loaded page leaves the block out while no basket
 * is published: it is below the fold, so the guess moves nothing on screen.
 *
 * What the page cannot know before its data, it draws as the designed
 * default, Home with a portfolio: a portfolio card. Once the page commits,
 * that slot is already right (the loader waited for it); only this pending
 * page guesses.
 *
 * The watchlist has no slot here at all: its loaded list lives in the RAIL
 * (`HomeRail`), and its empty and failed cards hang on a read only the
 * loader settles.
 *
 * Bars only, no words, not even the card titles — nothing here can be
 * mistaken for, or found by a test as, the loaded content.
 */

/** A `SectionHead` row at the section rung (20px, a 28px line) with a "See all"-shaped mark at its
 *  end: the head of Gainers and losers, Baskets, the issues table and Latest. */
function SectionHeadRow({
  title,
  action = 'h-4 w-16',
}: {
  title: string
  action?: string | null | undefined
}) {
  return (
    <div className="flex h-7 items-center justify-between gap-3">
      <Skeleton className={cn('h-5 rounded-xs', title)} />
      {action === null ? null : <Skeleton className={cn('rounded-xs', action)} />}
    </div>
  )
}

/** A block whose head sits outside its body (Gainers and losers, the issues, Baskets, Latest):
 *  the section head row, then the body 12px under it. */
function OutsideHead({
  title,
  gap = 'gap-3',
  action,
  children,
}: {
  title: string
  gap?: string
  /** The head's right end, as `SectionHeadRow` takes it. */
  action?: string | null | undefined
  children: ReactNode
}) {
  return (
    <div className={cn('flex flex-col', gap)}>
      <SectionHeadRow title={title} action={action} />
      {children}
    </div>
  )
}

/**
 * TodaysBriefCard's body, inside the bubble: the 22px headline on its 28px line (a second line
 * below `@md` of the column), the 20px summary lines under it 8px down — two, or one from `@2xl`
 * while the summary is templated (`BRIEF_API_AVAILABLE` false: one line at that width) — then
 * "Read the brief" 16px under those.
 */
export function BriefSkeleton() {
  return (
    <div>
      <Line line="h-7" bar="h-5 w-3/4 rounded-sm" />
      <Line line="h-7 @md:hidden" bar="h-5 w-1/3 rounded-sm" />
      <Line line="mt-2 h-5" bar="h-3 w-full max-w-160" />
      <Line line={cn('h-5', !BRIEF_API_AVAILABLE && '@2xl:hidden')} bar="h-3 w-2/3" />
      <Line line="mt-4 h-5" bar="h-4 w-28" />
    </div>
  )
}

/**
 * IndexTodayCard's body, under its title row: the level beside its move, the boxed period row
 * (six chips, 38px on a 40px row) across from them (wrapping under on a phone), the chart's own 212px box (plot
 * and time row), then the session strip.
 */
export function IndexTodayBodySkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <div className={cn(CHART_HEAD.root, 'min-h-10')}>
          <div className={CHART_HEAD.grid}>
            <div className={CHART_HEAD.info}>
              <div className="flex items-center gap-3">
                <Line line="h-9" bar="h-8 w-40 rounded-sm" />
                <Line line="h-6" bar="h-4 w-28" />
              </div>
            </div>
            <div className={CHART_HEAD.actions}>
              <div className={CHART_HEAD.bare}>
                <Skeleton className="h-10 w-76 rounded-sm" />
              </div>
            </div>
          </div>
        </div>
        <Skeleton className={cn(CHART_UNDER_HEAD, 'h-53 w-full rounded-md')} />
      </div>
      <StatStripSkeleton cells={3} surface="ruled" />
    </div>
  )
}

/** IndexTodayCard whole, for the pending page: the 20px title beside the date, 16px over the
 *  body. */
function IndexTodaySkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex h-7 items-center gap-2">
        <Line line="h-7" bar="h-5 w-32" />
        <Line line="h-5" bar="h-4 w-24" />
      </div>
      <IndexTodayBodySkeleton />
    </div>
  )
}

/** TodaysBriefCard whole: Mitra on the baseline (84×120; 56×80 narrow) beside the bubble. */
function BriefCardSkeleton() {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-end gap-4">
      <Skeleton className="h-20 w-14 rounded-md @md:h-30 @md:w-21" />
      <div className="mb-4 min-w-0 border border-border bg-card px-4 pt-4 pb-5 @md:px-6 @md:pt-5 @md:pb-6">
        <BriefSkeleton />
      </div>
    </div>
  )
}

/** HomeMoversCard: the 28px head, 16px under it the two sides — a 20px label over the five-row,
 *  44px-row table each, 8px apart, side by side (32px) from `@2xl` of the column as loaded. */
function MoversSkeleton() {
  return (
    <OutsideHead title="w-48" gap="gap-4">
      <div className="grid gap-8 @2xl:grid-cols-2">
        {[0, 1].map((side) => (
          <div key={side} className="flex flex-col gap-2">
            <Line line="h-5" bar="h-4 w-24" />
            <DataTableSkeleton rows={5} columns={3} rowHeight="md" />
          </div>
        ))}
      </div>
    </OutsideHead>
  )
}

/** Your portfolio whole, for the pending page: the hairline panel on its 20px inset, the 24px
 *  title row, 16px under it the body. */
function PortfolioFrame() {
  return (
    <div className="border border-border-subtle bg-card px-5 py-5">
      <div className="mb-4 flex h-6 items-center justify-between gap-3">
        <Skeleton className="h-4 w-32 rounded-xs" />
        <Skeleton className="h-3 w-18 rounded-xs" />
      </div>
      <PortfolioBodySkeleton />
    </div>
  )
}

/**
 * PortfolioCard's body: the market value (36) over the day's change (28) beside the curve at its
 * fixed `h-20` (the loaded card's own `CHART_ROW_H`), the two stacked below `@md` as loaded; then
 * the hairline 16px under and over, and the three centred book cells (a 16px label over a 28px
 * figure).
 */
export function PortfolioBodySkeleton() {
  return (
    <div className="@container">
      <div className="flex flex-col gap-4 @md:flex-row @md:items-center">
        <div className="flex flex-col gap-1 @md:shrink-0">
          <Skeleton className="h-9 w-44" />
          <Skeleton className="h-7 w-40" />
        </div>
        <Skeleton className="h-20 w-full rounded-md" />
      </div>
      <div className="my-4 h-px bg-border-subtle" aria-hidden="true" />
      <div className="flex flex-col gap-3 @md:flex-row @md:gap-6">
        {[0, 1, 2].map((cell) => (
          <div key={cell} className="flex flex-col items-center gap-1 @md:flex-1">
            <Line line="h-4" bar="h-3 w-16" />
            <Line line="h-7" bar="h-6 w-28" />
          </div>
        ))}
      </div>
    </div>
  )
}

/** One of the two panels, whole: the bordered panel, its 40px head band (title and a 12px link),
 *  then the rows. */
function HomePanelSkeleton({ title, leading = false }: { title: string; leading?: boolean }) {
  return (
    <div className="min-w-0 border border-border bg-card">
      <div className="flex h-10 items-center justify-between gap-3 border-b border-border bg-table-head px-4">
        <Skeleton className={cn('h-4 rounded-xs', title)} />
        <Skeleton className="h-3 w-8 rounded-xs" />
      </div>
      <PanelRowsSkeleton leading={leading} />
    </div>
  )
}

/** Baskets to try's cards before their data: four across from `@2xl` (two on a narrow column),
 *  12px apart, each the loaded card's hairline box around its 244px: 16 in, the 124px basket,
 *  12, the 20px name, 4, a two-line tagline (32), 4, the 16px count, 16. */
export function BasketsBodySkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-4">
      {Array.from({ length: FEATURED_BASKETS }, (_, index) => (
        <div key={index} className="border border-border-subtle">
          <Skeleton className="h-61 w-full" />
        </div>
      ))}
    </div>
  )
}

/** Latest from MoneyMitra's row before its data: the 16:9 post images at the loaded card's width,
 *  cut off at the column's edge, each over its two-line title and its 16px meta line. */
export function LatestBodySkeleton() {
  return (
    <div className="flex gap-4 overflow-hidden">
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="flex w-(--post-card-w) shrink-0 flex-col gap-1">
          <Skeleton className="mb-2 aspect-video w-full" />
          <Skeleton className="h-10 w-full rounded-xs" />
          <Line line="h-4" bar="h-3 w-32" />
        </div>
      ))}
    </div>
  )
}

/** Home's main column before the loader's reads have landed. See the header comment. The mock's
 *  first-run strip has no skeleton: it draws only for a member the page cannot know about yet,
 *  and it is the column's last block, so nothing moves when it lands. */
export function HomeSkeleton() {
  return (
    <>
      <IndexTodaySkeleton />
      <BriefCardSkeleton />
      <MoversSkeleton />
      <PortfolioFrame />
      <div className="grid gap-6 @2xl:grid-cols-2">
        <HomePanelSkeleton title="w-24" />
        <HomePanelSkeleton title="w-36" leading />
      </div>
      <OutsideHead title="w-36">
        <BasketsBodySkeleton />
      </OutsideHead>
      <OutsideHead title="w-72">
        <DataTableSkeleton rows={5} columns={6} rowHeight="lg" />
      </OutsideHead>
      <OutsideHead title="w-56" action="h-7 w-16">
        <LatestBodySkeleton />
      </OutsideHead>
    </>
  )
}
